import {
  buildPatientClinicalTasksSubscriptionCriteria,
  buildPatientCompletedClinicalTasksSearch,
} from '@bariendo/lib/clinical-tasks';
import { Patient, Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSubscriptionWithLimitHandling } from '../services/subscriptions';

/**
 * COMPLETED clinical tasks for one patient, feeding the timeline's
 * "Task completed" entries. Mirrors useClinicalTasks (request-id guard,
 * last-known-good on error) and shares the same status-free subscription,
 * so a completion appears — and an Undo removes it — live.
 */
export interface UseCompletedClinicalTasksResult {
  tasks: Task[];
  /** Refetch; wired into the timeline's composite refresh (see useTimeline). */
  refresh: () => Promise<void>;
}

export function useCompletedClinicalTasks(patient: Patient | undefined): UseCompletedClinicalTasksResult {
  const medplum = useMedplum();
  const [tasks, setTasks] = useState<Task[]>([]);
  const fetchRequestIdRef = useRef(0);

  const searchParams = useMemo(
    () => (patient ? buildPatientCompletedClinicalTasksSearch(patient) : undefined),
    [patient]
  );
  const subscriptionCriteria = useMemo(
    () => (patient ? buildPatientClinicalTasksSubscriptionCriteria(patient) : undefined),
    [patient]
  );

  const refresh = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;

    if (!medplum.isAuthenticated() || !searchParams) {
      setTasks([]);
      return;
    }

    try {
      // See useClinicalTasks: subscription-driven refetch must not be served
      // from the 60s GET cache.
      const results = await medplum.searchResources('Task', searchParams, { cache: 'reload' });
      if (requestId === fetchRequestIdRef.current) {
        setTasks([...results]);
      }
    } catch {
      // Keep last-known-good; the next subscription tick retries.
    }
  }, [medplum, searchParams]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useSubscriptionWithLimitHandling(subscriptionCriteria ?? '', refresh);

  return { tasks, refresh };
}
